import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupCountBy, groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import { withId } from '@/pages/reports/reportHelpers';
import type { CustomerDispatchSummaryRow, DispatchOrderRegisterRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76', '#3e5c8a'];

export default function OutboundPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: dispatchOrders = [], isLoading: loadingDispatch } = useQuery({
    queryKey: ['reports', 'dispatch-order-register'],
    queryFn: async () => (await apiClient.get<DispatchOrderRegisterRow[]>('/reports/dispatch-order-register')).data,
  });
  const { data: customerSummary = [], isLoading: loadingCustomer } = useQuery({
    queryKey: ['reports', 'customer-dispatch-summary'],
    queryFn: async () => (await apiClient.get<CustomerDispatchSummaryRow[]>('/reports/customer-dispatch-summary')).data,
  });

  const totalOrders = useMemo(() => new Set(dispatchOrders.map((r) => r.dispatchOrderId)).size, [dispatchOrders]);
  const issuedQty = useMemo(() => dispatchOrders.reduce((sum, r) => sum + r.issuedQty, 0), [dispatchOrders]);
  const pendingQty = useMemo(() => dispatchOrders.reduce((sum, r) => sum + r.pendingQty, 0), [dispatchOrders]);
  const openOrders = useMemo(() => customerSummary.reduce((sum, r) => sum + r.openOrders, 0), [customerSummary]);

  const topCustomers = useMemo(() => topN(customerSummary, (r) => r.totalRequestedQty, 5).reverse(), [customerSummary]);
  const monthlyTrend = useMemo(() => groupSumByMonth(dispatchOrders, (r) => r.orderDate, [{ key: 'count', value: () => 1 }]), [dispatchOrders]);
  const statusDistribution = useMemo(() => groupCountBy(dispatchOrders, (r) => r.status), [dispatchOrders]);

  const customerColumns: DataTableColumn<ReturnType<typeof withId<CustomerDispatchSummaryRow>>[number]>[] = [
    { field: 'customerName', headerName: tr('columns.customer') },
    { field: 'totalOrders', headerName: t('outbound.totalOrders'), align: 'right' },
    { field: 'openOrders', headerName: t('outbound.openOrders'), align: 'right' },
    { field: 'totalRequestedQty', headerName: tr('columns.requestedQty'), align: 'right' },
    { field: 'pendingQty', headerName: tr('columns.pendingQty'), align: 'right' },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('outbound.totalOrders')} value={totalOrders} icon={<LocalShippingIcon />} accentColor={colors.outbound} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('outbound.issuedQty')} value={issuedQty.toLocaleString()} icon={<Inventory2Icon />} accentColor={colors.outbound} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={tr('columns.pendingQty')} value={pendingQty.toLocaleString()} icon={<PendingActionsIcon />} accentColor={colors.outbound} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('outbound.openOrders')} value={openOrders} icon={<StorefrontIcon />} accentColor={colors.outbound} />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={6}>
          <ChartCard title={t('outbound.topCustomersTitle')} loading={loadingCustomer} empty={!loadingCustomer && topCustomers.length === 0}>
            <DashboardBarChart
              layout="horizontal"
              categories={topCustomers.map((c) => c.customerName)}
              series={[{ label: tr('columns.requestedQty'), data: topCustomers.map((c) => c.totalRequestedQty), color: colors.outbound }]}
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={6}>
          <ChartCard title={t('outbound.statusTitle')} loading={loadingDispatch} empty={!loadingDispatch && statusDistribution.length === 0}>
            <DashboardPieChart data={statusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <ChartCard title={t('outbound.trendTitle')} loading={loadingDispatch} empty={!loadingDispatch && monthlyTrend.length === 0} height={260}>
        <DashboardLineChart
          categories={monthlyTrend.map((m) => m.month)}
          series={[{ label: t('outbound.totalOrders'), data: monthlyTrend.map((m) => m.values.count), color: colors.outbound, area: true }]}
          height={260}
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('outbound.customerTableTitle')}
        </Typography>
        <DataTable columns={customerColumns} rows={withId(customerSummary, (r) => r.customerId)} loading={loadingCustomer} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
    </Stack>
  );
}
